import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const GoogleBtn = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={go} type="button" data-testid="google-login-btn" className="flex w-full items-center justify-center gap-2 border border-border py-2.5 text-sm font-medium transition-colors hover:bg-cream">
      <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
      Continue with Google
    </button>
  );
};

const Auth = ({ mode }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginSuccess, user } = useStore();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(user.role === "admin" ? "/admin" : "/account"); }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const r = await api.post("/auth/login", { email: form.email, password: form.password });
        loginSuccess(r.data); toast.success("Welcome back!");
        navigate(r.data.user.role === "admin" ? "/admin" : "/account");
      } else if (mode === "register") {
        const r = await api.post("/auth/register", form);
        loginSuccess(r.data); toast.success("Account created!"); navigate("/account");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If an account exists, a reset link has been sent (check server logs in demo).");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token"), password: form.password });
        toast.success("Password reset! Please log in."); navigate("/login");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setLoading(false); }
  };

  const titles = { login: "Welcome Back", register: "Create Account", forgot: "Reset Password", reset: "Set New Password" };

  return (
    <div className="mx-auto flex max-w-md flex-col px-6 py-16" data-testid="auth-page">
      <div className="mx-auto mb-6"><Logo size="md" /></div>
      <h1 className="text-center font-serif text-4xl text-burgundy">{titles[mode]}</h1>
      <p className="mt-2 text-center font-script text-2xl text-charcoal/60">Style That Speaks</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        {mode === "register" && (<div><Label>Full Name</Label><Input required value={form.name} onChange={set("name")} data-testid="name-input" className="mt-1 rounded-none" /></div>)}
        {(mode === "login" || mode === "register" || mode === "forgot") && (<div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="email-input" className="mt-1 rounded-none" /></div>)}
        {mode === "register" && (<div><Label>Mobile Number</Label><Input value={form.phone} onChange={set("phone")} data-testid="phone-input" className="mt-1 rounded-none" /></div>)}
        {(mode === "login" || mode === "register" || mode === "reset") && (<div><Label>Password</Label><Input type="password" required value={form.password} onChange={set("password")} data-testid="password-input" className="mt-1 rounded-none" /></div>)}
        <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full rounded-none bg-burgundy uppercase tracking-widest">
          {loading ? "Please wait..." : mode === "login" ? "Login" : mode === "register" ? "Register" : mode === "forgot" ? "Send Reset Link" : "Reset Password"}
        </Button>
      </form>
      {mode === "login" && <Link to="/forgot-password" className="mt-3 block text-center text-sm text-muted-text hover:text-burgundy">Forgot password?</Link>}
      {(mode === "login" || mode === "register") && (
        <>
          <div className="my-6 flex items-center gap-3 text-xs text-muted-text"><span className="h-px flex-1 bg-border" />OR<span className="h-px flex-1 bg-border" /></div>
          <GoogleBtn />
        </>
      )}
      <p className="mt-6 text-center text-sm text-muted-text">
        {mode === "login" ? <>New to ANUSWA? <Link to="/register" className="text-burgundy hover:underline">Create an account</Link></> : <>Already have an account? <Link to="/login" className="text-burgundy hover:underline">Login</Link></>}
      </p>
    </div>
  );
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginSuccess } = useStore();
  useEffect(() => {
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { navigate("/login"); return; }
    api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } })
      .then((r) => { loginSuccess(r.data); window.history.replaceState(null, "", "/account"); toast.success("Signed in with Google"); navigate("/account"); })
      .catch(() => { toast.error("Google sign-in failed"); navigate("/login"); });
  }, []);
  return <div className="grid place-items-center py-32 font-serif text-2xl text-burgundy">Signing you in...</div>;
};

export default Auth;
